/**
 * Non-delivery idle per van for one day (zeecore /telematics/v1/vehicle-day-stops):
 * stops that were neither at a customer (a GPS site visit) nor at a depot, at
 * least the org's idle threshold long — with engine idling and the driver's
 * tachograph break shown beside each, so a legal break is told apart from the
 * rest of the stop. Describes; it does not judge.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { fetchVehicleDayStops } from '../../services/vehicleDayStopsClient';
import type { VehicleDayStopsResponse } from '../../types/vehicleDayStops';
import { describeIdleStop, formatMinutes, formatTachoBreakCell, previousLondonDate, sortByIdle } from '../../utils/vehicleIdlePresentation';

export default function VehicleIdlePanel() {
  const [date, setDate] = useState(() => previousLondonDate());
  const [data, setData] = useState<VehicleDayStopsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    fetchVehicleDayStops(date, { signal: ctrl.signal })
      .then((r) => setData(r))
      .catch((e: unknown) => {
        if (!ctrl.signal.aborted) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, [date]);

  const vehicles = useMemo(() => sortByIdle(data?.vehicles ?? []), [data]);
  const threshold = vehicles[0]?.thresholds.idle_min_minutes;
  const totals = useMemo(() => vehicles.reduce((t, v) => ({
    stops: t.stops + v.summary.non_delivery_idle_stops,
    minutes: t.minutes + v.summary.non_delivery_idle_minutes,
    engine: t.engine + v.summary.non_delivery_engine_idle_minutes,
  }), { stops: 0, minutes: 0, engine: 0 }), [vehicles]);

  return (
    <section className="mt-6 rounded-xl border border-gray-700 bg-gray-800/60 p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-white">Stops away from customers and depots</h3>
          <p className="text-xs text-gray-400 mt-1 max-w-2xl">
            From each vehicle&apos;s GPS: stops of {threshold ?? 10} minutes or more that were not at a customer delivery or
            collection, and not at a depot. Stationary time includes engine off; engine idling is stationary with the engine
            running. Tachograph break is what the driver&apos;s card recorded as break or rest during the stop. Samsara gets the
            tachograph only when the card or vehicle unit is downloaded, so for recent days part of the time may not be
            downloaded yet; that time is shown as such, not as no break. A collection is linked to the vehicle that stopped at its address; where several vehicles stopped there, it can still appear here.
          </p>
        </div>
        <label className="text-xs text-gray-400">
          Day{' '}
          <input
            type="date"
            value={date}
            max={previousLondonDate(new Date(Date.now() + 86_400_000))}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            className="ml-1 rounded bg-gray-900 border border-gray-600 px-2 py-1 text-gray-100"
          />
        </label>
      </div>

      {loading && <p className="text-sm text-gray-400 mt-4">Loading stops…</p>}
      {error && <p className="text-sm text-rose-300 mt-4">Could not load stops: {error}</p>}
      {!loading && !error && data && !data.computed && (
        <p className="text-sm text-gray-400 mt-4">
          Not worked out for this day yet. Stops are classified overnight, once the day&apos;s full GPS is in (around 05:30).
        </p>
      )}

      {!loading && !error && data?.computed && (
        <>
          <p className="text-sm text-gray-200 mt-4 tabular-nums">
            {totals.stops.toLocaleString()} stops · {formatMinutes(totals.minutes)} stationary · {formatMinutes(totals.engine)} engine idling
            <span className="text-gray-500"> across {vehicles.length} vehicles with GPS</span>
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-3">Vehicle</th>
                  <th className="py-2 pr-3 text-right">Stops</th>
                  <th className="py-2 pr-3 text-right">Customer</th>
                  <th className="py-2 pr-3 text-right">Depot</th>
                  <th className="py-2 pr-3 text-right">Away</th>
                  <th className="py-2 pr-3 text-right">Stationary</th>
                  <th className="py-2 pr-3 text-right">Engine idling</th>
                  <th className="py-2 pr-3 text-right">Tachograph break</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v) => {
                  const s = v.summary;
                  const expanded = open === v.vehicle_key;
                  const idleStops = v.stops.filter((x) => x.non_delivery_idle);
                  return (
                    <Fragment key={v.vehicle_key}>
                      <tr
                        className="border-t border-gray-700/60 cursor-pointer hover:bg-gray-700/30"
                        onClick={() => setOpen(expanded ? null : v.vehicle_key)}
                      >
                        <td className="py-2 pr-3 text-gray-100">{v.registration ?? v.vehicle_key}</td>
                        <td className="py-2 pr-3 text-right tabular-nums text-gray-300">{s.stops}</td>
                        <td className="py-2 pr-3 text-right tabular-nums text-gray-300">{s.customer_stops}</td>
                        <td className="py-2 pr-3 text-right tabular-nums text-gray-300">{s.depot_stops}</td>
                        <td className={`py-2 pr-3 text-right tabular-nums ${s.non_delivery_idle_stops > 0 ? 'text-amber-200' : 'text-gray-500'}`}>{s.non_delivery_idle_stops}</td>
                        <td className="py-2 pr-3 text-right tabular-nums text-gray-200">{formatMinutes(s.non_delivery_idle_minutes)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums text-gray-300">{formatMinutes(s.non_delivery_engine_idle_minutes)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums text-gray-300">
                          {v.tachograph_read && s.non_delivery_tacho_break_minutes !== undefined
                            ? formatTachoBreakCell(s)
                            : <span className="text-gray-500" title="No tachograph data was found for this vehicle's driver on this day">not read</span>}
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="bg-gray-900/40">
                          <td colSpan={8} className="px-3 py-2">
                            {idleStops.length === 0 ? (
                              <p className="text-xs text-gray-500">No stops away from customers and depots.</p>
                            ) : (
                              <ul className="space-y-1">
                                {idleStops.map((stop) => (
                                  <li key={stop.started_at} className="text-xs text-gray-300 tabular-nums">
                                    {describeIdleStop(stop)}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
